/* eslint-disable no-control-regex -- for sanitizeForLogging i*/
/* eslint-disable @typescript-eslint/no-extraneous-class -- need class in this way */
/* eslint-disable no-console -- Console logging is intentional for client-side debugging and fallback */
import {ApplicationInsights} from '@microsoft/applicationinsights-web'
import {env} from '@/services/env/client'

class ClientLoggerFactory {
	static instance: ApplicationInsights

	private constructor() {
		throw new Error('Use ClientLoggerFactory.getInstance()')
	}

	static getInstance(): ApplicationInsights {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it is the correct condition
		if (!ClientLoggerFactory.instance) {
			const connectionString =
				env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING
			if (!connectionString) {
				throw new Error(
					'Application Insights connection string is not defined.'
				)
			}
			ClientLoggerFactory.instance = new ApplicationInsights({
				config: {
					connectionString,
					enableAutoRouteTracking: true,
					enableDebug: true,
					loggingLevelConsole: 2,
				},
			})
			ClientLoggerFactory.instance.loadAppInsights()
		}
		return ClientLoggerFactory.instance
	}

	static enableTelemetry(): void {
		const instance = ClientLoggerFactory.getInstance()
		instance.config.disableTelemetry = false
	}

	static disableTelemetry(): void {
		const instance = ClientLoggerFactory.getInstance()
		instance.config.disableTelemetry = true
	}

	static isTelemetryEnabled(): boolean {
		const instance = ClientLoggerFactory.getInstance()
		return !instance.config.disableTelemetry
	}
}

function trackPageView(): void {
	ClientLoggerFactory.getInstance().trackPageView()
}

export function disablePageView(): void {
	ClientLoggerFactory.disableTelemetry()
}

export function enablePageView(): void {
	ClientLoggerFactory.enableTelemetry()
	trackPageView()
}

export function sanitizeForLogging(input: string): string {
	return input
		.replace(/[\x00-\x1F\x7F-\x9F]/g, '')
		.replace(/[\r\n\t]+/g, ' ')
		.slice(0, 1000)
}

export function logError(exception: unknown): void {
	if (ClientLoggerFactory.isTelemetryEnabled()) {
		if (exception instanceof Error) {
			console.error(exception)
			ClientLoggerFactory.getInstance().trackException({exception})
		} else if (typeof exception === 'object') {
			console.error(sanitizeForLogging(JSON.stringify(exception)))
			ClientLoggerFactory.getInstance().trackException({
				exception: new Error(JSON.stringify(exception)),
			})
		} else {
			console.error(sanitizeForLogging(String(exception)))
			ClientLoggerFactory.getInstance().trackException({
				exception: new Error(String(exception)),
			})
		}
	}
}

export function logMessage(
	message: string,
	properties?: Record<string, unknown>
): void {
	if (ClientLoggerFactory.isTelemetryEnabled()) {
		if (properties) {
			ClientLoggerFactory.getInstance().trackTrace({message}, properties)
		} else {
			ClientLoggerFactory.getInstance().trackTrace({message})
		}
	}
}

export function logEvent(event: {name: string; properties: object}): void {
	trackEvent({name: event.name}, event.properties as Record<string, unknown>)
}

export function trackEvent(
	event: {name: string},
	properties?: Record<string, unknown>,
	measurements?: Record<string, number>
): void {
	// Telemetry is best effort: never throw if client initialization or send fails.
	try {
		if (!ClientLoggerFactory.isTelemetryEnabled()) return

		const telemetryEvent = measurements
			? {
					name: event.name,
					properties,
					measurements,
				}
			: {
					name: event.name,
					properties,
				}

		ClientLoggerFactory.getInstance().trackEvent(telemetryEvent)
	} catch {
		// Intentionally swallow telemetry errors.
	}
}
